export default {
  id: 'pre-trade-coaching',
  title: 'Pre-Trade Coaching',
  content: `## Pre-Trade Coaching

Pre-trade coaching is the practice of reviewing your trade idea with Nyx **before** placing the order — not after. This is where Nyx delivers the most value.

Most traders lose discipline at the moment of decision — when the chart is moving and the temptation to enter is strongest. Having a structured pre-trade conversation slows you down and surfaces the checks you might skip.

---

## How to Use It

**Open Nyx AI Coach → Coach Mode or Trader Mode**

Tell Nyx what you are thinking:

> *"I want to buy INFY. It just broke its 20-day high on good volume. RSI is 58. Trend is up. Entry at ₹1,840, stop at ₹1,800, target ₹1,920."*

Nyx will review your plan and respond with:
- Whether the setup context supports an entry
- Whether your stop placement makes sense relative to ATR and key levels
- Whether your R:R meets your own stated minimum from your Risk Profile
- Any behavioral flags — e.g., if you have been overtrading this week
- A structured verdict: proceed, wait for confirmation, or avoid

---

## The Pre-Trade Checklist Nyx Works Through

Nyx internally checks your trade against these factors:

| Check | What Nyx looks at |
|---|---|
| Trend alignment | Is the stock in an UPTREND (above SMA50)? |
| Signal validity | Has a relevant scanner fired recently? |
| RSI context | Is RSI in a supportive range for this entry type? |
| Volume confirmation | Is volume above average — is there conviction? |
| Risk:Reward | Is your stated R:R ≥ your Risk Profile minimum? |
| Stop quality | Is your stop at least 1× ATR from entry? |
| Position size | Does the implied risk stay within your Risk Per Trade %? |
| Behavioral pattern | Are you in a revenge trade cycle? Have you already hit your daily loss limit? |

---

## Trader Mode — Structured Trade Plan

If you open Trader Mode and provide a symbol, Nyx returns a fully structured trade plan for that stock based on current data:

| Output | Description |
|---|---|
| Setup assessment | What the current chart, indicators, and scanners show |
| Entry range | Suggested entry zone with reasoning |
| Stop loss | Suggested stop with reference to ATR and key levels |
| Target | Suggested first target and extended target |
| Invalidation | The condition that would mean the thesis is wrong |
| Conviction | LOW / MEDIUM / HIGH based on signal alignment |

> Use Trader Mode to generate the initial analysis. Then challenge it — do you agree with the invalidation level? Does the entry range feel right to you?

---

## Example Interaction

**You:** *RELIANCE broke above ₹2,900 today — the 20-day high. Volume is 2.2× average. Should I enter?*

**Nyx might respond:** *The breakout is on strong volume — that is a positive. RSI is at 68, which is not yet extended but is approaching the upper range. The trend is confirmed UPTREND. The key question is whether you are entering at the breakout point or chasing — what was your entry at ₹2,900 relative to where the signal fired?*

---

## After the Trade

Nyx can also review completed trades retrospectively. Ask it to look at a specific closed trade and it will explain what the signals looked like at entry, what the outcome was, and what a disciplined trader would have done differently — if anything.`,
};
